/* eslint-disable max-classes-per-file */
import {
  SwapSDK as ChainflipSDK,
  type ChainflipNetwork,
  type QuoteResponseV2,
  type SwapStatusResponseV2,
  type ChainData,
  type ChainsAndAssets,
} from '@chainflip/sdk/swap';
import * as base58 from '@chainflip/utils/base58';
import {
  getAnyInternalAsset,
  type ChainflipChain,
  type AnyChainflipChain,
  anyAssetConstants,
  anyInternalAssetToRpcAsset,
  isChainflipAsset,
  readAssetValue,
  type AssetSymbol,
} from '@chainflip/utils/chainflip';
import { isTruthy } from '@chainflip/utils/guard';
import { isHex } from '@chainflip/utils/string';
import {
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferInstruction,
  getAccount,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import { type WalletContextState } from '@solana/wallet-adapter-react';
import { PublicKey, SystemProgram, Transaction, type Connection } from '@solana/web3.js';
import { isAxiosError } from 'axios';
import BigNumber from 'bignumber.js';
import { type Account, erc20Abi, type WalletClient, type Transport, type Chain } from 'viem';
import { type AnyChainId, chainById, type ChainId } from '@/shared/assets/chains';
import { FlipLogo } from '@/shared/assets/token-logos';
import { isChainflipTokenOrChain, type ChainflipToken } from '@/shared/assets/tokens';
import { NATIVE_TOKEN_ADDRESS } from '@/shared/assets/tokens/constants';
import { executeTronTransfer, type TronWeb } from '@/shared/components/TronConnect';
import { EGRESS_FEE, feeTypeMap, INGRESS_FEE, USE_DCA_V2 } from '@/shared/constants';
import {
  type ExecuteSwapResponse,
  type ChainflipRouteResponse,
  type ChainflipStatusResponse,
  type RouteRequest,
  type RouteResponseStep,
  type SwapStatus,
} from '@/shared/integrations/index';
import { type BaseIntegration, getDeterministicRouteId } from '@/shared/integrations/manager';
import {
  loadRouteFromLocalStorage,
  storeDepositChannelIdInLocalStorage,
  storeRouteInLocalStorage,
} from '@/shared/integrations/storage';
import { compareChains } from '@/shared/utils/chainflip';
import { convertFokMinPriceToReceivedAmount } from '@/shared/utils/convertFokMinPriceToReceivedAmount';
import {
  chainflipChainMap,
  chainflipAssetMap,
  getChainflipAsset,
  getChainflipNetwork,
} from '@/shared/utils/env';
import { entries } from '@/shared/utils/objects';
import { getEgressAmount, getSwapDuration, isDepositRejected } from '@/shared/utils/sdk';
import TokenAmount from '@/shared/utils/TokenAmount';

type SwapFee = QuoteResponseV2['quotes'][number]['includedFees'][number];

const depositChannelIdRegex = /^(?<issuedBlock>\d+)-(?<srcChain>[a-z]+)-(?<channelId>\d+)$/i;
const transactionHashRegex = /^0x[a-f\d]+$/i;

const chainflipStatusMap: Record<SwapStatusResponseV2['state'], SwapStatus | undefined> = {
  WAITING: 'waiting_for_src_tx',
  RECEIVING: 'waiting_for_src_tx_confirmation',
  SWAPPING: 'waiting_for_dest_tx',
  SENDING: 'waiting_for_dest_tx',
  SENT: 'completed',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

const mapChainIdToChainflip = (chainId: AnyChainId): AnyChainflipChain | undefined => {
  const chain = chainById(chainId);

  return isChainflipTokenOrChain(chain) ? chain.chainflipId : undefined;
};

const mapChainflipChain = (chainflipChain: ChainflipChain): ChainId => {
  if (chainflipChain in chainflipChainMap) {
    return chainflipChainMap[chainflipChain as keyof typeof chainflipChainMap].id;
  }

  throw new Error(`unexpected chain from chainflip sdk: ${chainflipChain}`);
};

const mapChainflipAsset = (
  chainflipChain: AnyChainflipChain,
  chainflipAsset: AssetSymbol,
): ChainflipToken => {
  const chainflipId = getAnyInternalAsset({ asset: chainflipAsset, chain: chainflipChain });

  if (chainflipId in chainflipAssetMap) {
    return chainflipAssetMap[chainflipId as keyof typeof chainflipAssetMap];
  }

  throw new Error(`unexpected asset from chainflip sdk: ${chainflipId}`);
};

const mapChainflipStatus = (response: SwapStatusResponseV2): SwapStatus => {
  if (isDepositRejected({ integration: 'chainflip', integrationData: response })) {
    return 'completed';
  }
  return chainflipStatusMap[response.state] ?? 'unknown';
};

export const cfIsBoostedChannel = (sdkStatus: SwapStatusResponseV2 | undefined) =>
  Boolean(sdkStatus && sdkStatus.boost?.maxBoostFeeBps && sdkStatus.boost.maxBoostFeeBps > 0);

export const cfIsBoostSkipped = (
  sdkStatus: SwapStatusResponseV2 | undefined,
): sdkStatus is SwapStatusResponseV2 & {
  boostSkippedAt: number;
  boostSkippedBlockIndex: string;
} => Boolean(sdkStatus && sdkStatus.boost?.skippedAt);

export const cfIsSwapBoosted = (
  sdkStatus: SwapStatusResponseV2 | undefined,
): sdkStatus is SwapStatusResponseV2 & {
  depositBoostedAt: number;
  depositBoostedBlockIndex: string;
} => Boolean(sdkStatus && sdkStatus.boost?.boostedAt);

type CompleteState = 'SENDING' | 'SENT' | 'COMPLETED' | 'FAILED';

type GetMatchingState<T, U> = T extends { state: U } ? T : never;

export const cfIsSwappingFinished = (
  sdkStatus: SwapStatusResponseV2 | undefined,
): sdkStatus is GetMatchingState<SwapStatusResponseV2, CompleteState> =>
  sdkStatus != null &&
  (sdkStatus.state === 'SENDING' ||
    sdkStatus.state === 'SENT' ||
    sdkStatus.state === 'COMPLETED' ||
    sdkStatus.state === 'FAILED');

const shouldUseBoostQuote = (sdkStatus: SwapStatusResponseV2 | undefined) =>
  cfIsBoostedChannel(sdkStatus) && !cfIsBoostSkipped(sdkStatus);

export const calculateMinPrice = (
  quote: ChainflipRouteResponse['integrationData']['quote'],
  effectiveSlippageTolerancePercent: number,
) => {
  if (!quote?.estimatedPrice) {
    throw new Error(`Missing quotedPrice when calculating minimum price`);
  }

  return BigNumber(quote.estimatedPrice)
    .multipliedBy(1 - effectiveSlippageTolerancePercent / 100)
    .toFixed();
};

export const calculateMinReceived = (route: ChainflipRouteResponse, minPrice: string) =>
  convertFokMinPriceToReceivedAmount({
    srcAmount: route.srcAmount,
    ingressFee:
      route.platformFees.find((fee) => fee.name === INGRESS_FEE)?.amount ?? new TokenAmount(0),
    egressFee:
      route.platformFees.find((fee) => fee.name === EGRESS_FEE)?.amount ?? new TokenAmount(0),
    minPrice,
    destTokenDecimals: route.destToken.decimals,
  });

const getAssetsAndAmount = (route: ChainflipRouteResponse) => ({
  srcChain: anyAssetConstants[route.srcToken.chainflipId].chain as ChainflipChain,
  srcAsset: anyInternalAssetToRpcAsset[route.srcToken.chainflipId].asset,
  destChain: anyAssetConstants[route.destToken.chainflipId].chain as ChainflipChain,
  destAsset: anyInternalAssetToRpcAsset[route.destToken.chainflipId].asset,
  amount: route.srcAmount.toString(),
});

const buildRouteObject = (
  swapData: ChainsAndAssets & { amount: string },
  quoteData:
    | QuoteResponseV2['quotes'][number]
    | {
        intermediateAmount?: string;
        egressAmount: string;
        includedFees: SwapFee[];
        estimatedDurationSeconds: number;
        estimatedPrice?: string;
        maxBoostFeeBps?: number;
        dcaParams?: {
          numberOfChunks: number;
          chunkIntervalBlocks: number;
        };
      },
  boostData: { defaultDurationSeconds: number; hasBoostQuote: boolean },
): ChainflipRouteResponse => {
  const srcToken = mapChainflipAsset(swapData.srcChain, swapData.srcAsset);
  const srcAmount = new TokenAmount(swapData.amount, srcToken.decimals);
  const destToken = mapChainflipAsset(swapData.destChain, swapData.destAsset);
  const destAmount = new TokenAmount(quoteData.egressAmount, destToken.decimals);

  const intermediateUsdcAmount = quoteData.intermediateAmount
    ? new TokenAmount(quoteData.intermediateAmount, chainflipAssetMap.Usdc.decimals)
    : undefined;

  const steps: RouteResponseStep<ChainflipToken>[] = intermediateUsdcAmount
    ? [
        {
          protocolName: 'Chainflip',
          protocolLink: undefined,
          srcToken,
          srcAmount,
          destAmount: intermediateUsdcAmount,
          destToken: chainflipAssetMap.Usdc,
        },
        {
          protocolName: 'Chainflip',
          protocolLink: undefined,
          srcToken: chainflipAssetMap.Usdc,
          srcAmount: intermediateUsdcAmount,
          destToken,
          destAmount,
        },
      ]
    : [
        {
          protocolName: 'Chainflip',
          protocolLink: undefined,
          srcToken,
          srcAmount,
          destToken,
          destAmount,
        },
      ];

  const platformFees = entries(feeTypeMap)
    .filter(([, name]) => name)
    .flatMap(([type, name]) =>
      quoteData.includedFees
        .filter((fee) => fee.type === type)
        .map((fee) => {
          const token = mapChainflipAsset(fee.chain, fee.asset);

          return {
            name: name as string,
            token,
            amount: new TokenAmount(fee.amount, token.decimals),
          };
        }),
    );

  const routeResponse = {
    integration: 'chainflip' as const,
    integrationData: {
      isBoosted: Boolean('maxBoostFeeBps' in quoteData && quoteData.maxBoostFeeBps),
      hasBoostQuote: boostData.hasBoostQuote,
      defaultDurationSeconds: boostData.defaultDurationSeconds,
      dcaParams: 'dcaParams' in quoteData ? quoteData?.dcaParams : undefined,
      quote: 'type' in quoteData ? quoteData : undefined,
    },
    srcToken,
    srcAmount,
    destToken,
    destAmount,
    steps,
    gasFees: [],
    platformFees,
    durationSeconds: quoteData.estimatedDurationSeconds,
  };

  return { ...routeResponse, id: getDeterministicRouteId(routeResponse) };
};

const transactionHashKey = (swapId: string) => `chainflip-deposit-transaction-${swapId}`;

const storeTransactionHashInLocalStorage = (swapId: string, txHash: string) => {
  localStorage.setItem(transactionHashKey(swapId), txHash);
};
const loadTransactionHashFromLocalStorage = (swapId: string): string | undefined =>
  localStorage.getItem(transactionHashKey(swapId)) ?? undefined;

const isBase58 = (str: string | undefined) => {
  if (!str) return false;

  try {
    base58.decode(str);
    return true;
  } catch {
    return false;
  }
};

// Tron transaction hashes are 64-char hex strings without a 0x prefix
const isTronTxHash = (str: string | undefined): str is string =>
  Boolean(str && /^[a-f0-9]{64}$/i.test(str));

const sortChains = (a: ChainData, b: ChainData) => compareChains(a.chain, b.chain);

const getBrokerAccount = () => {
  const account = process.env.NEXT_PUBLIC_CHAINFLIP_BROKER_ACCOUNT;

  return account?.startsWith('cF') ? (account as `cF${string}`) : undefined;
};

class SDKWithCommission extends ChainflipSDK {
  // eslint-disable-next-line class-methods-use-this
  protected override shouldTakeCommission(): boolean {
    return process.env.NEXT_PUBLIC_APP_NAME?.toLowerCase() === 'swap';
  }
}

export class ChainflipIntegration implements BaseIntegration {
  sdk = new SDKWithCommission({
    network: getChainflipNetwork() as ChainflipNetwork,
    backendUrl: process.env.NEXT_PUBLIC_CHAINFLIP_BACKEND_URL,
    rpcUrl:
      process.env.NEXT_PUBLIC_STATECHAIN_NODE_URI ??
      process.env.NEXT_PUBLIC_STATECHAIN_RPC_HTTP_URL,
    enabledFeatures: { dcaV2: USE_DCA_V2 },
    brokerAccount: getBrokerAccount(),
  });

  readonly name = 'Chainflip';

  readonly logo = FlipLogo;

  getChains = async () => {
    const sdkChains = await this.sdk.getChains();

    return sdkChains
      .sort(sortChains)
      .map((chain) => chainById(mapChainflipChain(chain.chain)))
      .filter(isTruthy);
  };

  getDestinationChains = async (srcChainId: ChainId) => {
    const chainflipChain = mapChainIdToChainflip(srcChainId);
    const sdkChains = (await this.sdk.getChains(chainflipChain as ChainflipChain)) ?? [];

    return sdkChains
      .sort(sortChains)
      .map((chain) => chainById(mapChainflipChain(chain.chain)))
      .filter(isTruthy);
  };

  getRoutes = async (routeParams: RouteRequest) => {
    const srcChain = mapChainIdToChainflip(routeParams.srcChainId);
    const srcChainflipAsset = getChainflipAsset(
      routeParams.srcChainId,
      routeParams.srcTokenAddress,
    );
    const srcAsset = srcChainflipAsset
      ? anyInternalAssetToRpcAsset[srcChainflipAsset].asset
      : undefined;
    const destChain = mapChainIdToChainflip(routeParams.destChainId);
    const destChainflipAsset = getChainflipAsset(
      routeParams.destChainId,
      routeParams.destTokenAddress,
    );
    const destAsset = destChainflipAsset
      ? anyInternalAssetToRpcAsset[destChainflipAsset].asset
      : undefined;

    const { minimumAmount, maximumAmount } = await this.getSwapLimits(
      routeParams.srcChainId,
      routeParams.srcTokenAddress,
    );
    const { amount } = routeParams;

    if (
      !srcChain ||
      !srcAsset ||
      !destChain ||
      !destAsset ||
      amount < minimumAmount ||
      (maximumAmount && amount > maximumAmount)
    ) {
      return [];
    }

    const sdkQuote = await this.sdk.getQuoteV2({
      srcChain: srcChain as ChainflipChain,
      destChain: destChain as ChainflipChain,
      srcAsset,
      destAsset,
      amount: amount.toString(),
      isOnChain: routeParams.isOnChain,
    });

    return sdkQuote.quotes
      .flatMap((quote) => [
        buildRouteObject(sdkQuote, quote, {
          defaultDurationSeconds: quote.estimatedDurationSeconds,
          hasBoostQuote: Boolean(quote.boostQuote),
        }),
        quote.boostQuote &&
          buildRouteObject(sdkQuote, quote.boostQuote, {
            defaultDurationSeconds: quote.estimatedDurationSeconds,
            hasBoostQuote: true,
          }),
      ])
      .filter(isTruthy);
  };

  getTokens = async (
    chainId: AnyChainId,
    type?: 'deposit' | 'destination',
  ): Promise<ChainflipToken[]> => {
    const chainflipChain = mapChainIdToChainflip(chainId) as ChainflipChain;
    if (!chainflipChain) return [];

    const sdkAssets = await this.sdk.getAssets(chainflipChain, type);

    return sdkAssets.map((asset) => mapChainflipAsset(asset.chain, asset.asset));
  };

  async getSwapLimits(
    chainId: AnyChainId,
    tokenAddress: string,
  ): Promise<{ maximumAmount: bigint | null; minimumAmount: bigint }> {
    const limits = await this.sdk.getSwapLimits();

    const chainflipAsset = getChainflipAsset(chainId, tokenAddress);
    if (!chainflipAsset || !isChainflipAsset(chainflipAsset)) {
      return { minimumAmount: 0n, maximumAmount: null };
    }

    return {
      minimumAmount: readAssetValue(limits.minimumSwapAmounts, chainflipAsset),
      maximumAmount: readAssetValue(limits.maximumSwapAmounts, chainflipAsset),
    };
  }

  getStatus = async (swapId: string): Promise<ChainflipStatusResponse | undefined> => {
    const storedRoute = loadRouteFromLocalStorage('chainflip', swapId);
    const depositChannelId = depositChannelIdRegex.test(swapId)
      ? swapId
      : storedRoute?.depositChannelId;
    const localTransactionHash = transactionHashRegex.test(swapId)
      ? swapId
      : loadTransactionHashFromLocalStorage(swapId);

    const shareableId = depositChannelId ?? localTransactionHash;

    let sdkStatus: SwapStatusResponseV2 | undefined;

    if (shareableId) {
      try {
        sdkStatus = await this.sdk.getStatusV2({ id: shareableId });
      } catch (e) {
        // ignore 404: sdk will return status of transaction hash only after deposit is witnessed
        if (!(isAxiosError(e) && e.response?.status === 404)) throw e;
      }
    }

    let swapParams: Parameters<typeof buildRouteObject>[0];
    let useBoostQuote = shouldUseBoostQuote(sdkStatus);
    let destAddress = sdkStatus?.destAddress;

    if (sdkStatus && sdkStatus.state !== 'FAILED') {
      const srcAmount =
        'deposit' in sdkStatus
          ? sdkStatus.deposit.amount
          : (sdkStatus.depositChannel.expectedDepositAmount ?? '0');

      swapParams = {
        ...sdkStatus,
        srcChain: sdkStatus.srcChain as ChainflipChain,
        destChain: sdkStatus.destChain as ChainflipChain,
        amount: srcAmount,
      };
    } else if (sdkStatus?.state === 'FAILED') {
      // for smart contract failures, we don't know the destination
      if (!sdkStatus.destChain || !sdkStatus.destAsset) return undefined;

      swapParams = {
        ...sdkStatus,
        srcChain: sdkStatus.srcChain as ChainflipChain,
        destChain: sdkStatus.destChain as ChainflipChain,
        amount: sdkStatus.deposit.amount ?? '0',
      };
    } else if (storedRoute && storedRoute.integration === 'chainflip') {
      swapParams = getAssetsAndAmount(storedRoute);
      useBoostQuote = storedRoute.integrationData.isBoosted;
      destAddress = storedRoute.destAddress;
    } else {
      return undefined;
    }

    const fees =
      sdkStatus && 'fees' in sdkStatus && sdkStatus.fees
        ? sdkStatus.fees.filter((fee) => fee.type !== 'LIQUIDITY')
        : undefined;

    // get route data from status if the amounts are locked in, otherwise fetch fresh quote
    let route: ChainflipRouteResponse;
    if (sdkStatus && cfIsSwappingFinished(sdkStatus)) {
      route = buildRouteObject(
        swapParams,
        {
          intermediateAmount: sdkStatus.swap.swappedIntermediateAmount,
          egressAmount: getEgressAmount(sdkStatus) ?? '0',
          includedFees: fees ?? [],
          estimatedDurationSeconds: sdkStatus.estimatedDurationSeconds ?? 0,
          maxBoostFeeBps: sdkStatus.boost?.maxBoostFeeBps,
          dcaParams: sdkStatus.depositChannel?.dcaParams,
        },
        {
          defaultDurationSeconds: 0,
          hasBoostQuote: false,
        },
      );
    } else {
      const numberOfChunks =
        storedRoute?.integration === 'chainflip'
          ? storedRoute.integrationData.dcaParams?.numberOfChunks
          : sdkStatus?.depositChannel?.dcaParams?.numberOfChunks;

      const sdkQuotes = await this.sdk.getQuoteV2(swapParams);
      const sdkQuote =
        sdkQuotes.quotes.find((q) => {
          const quoteChunks = q.type === 'DCA' ? q.dcaParams?.numberOfChunks : undefined;
          return quoteChunks === numberOfChunks;
        }) ?? sdkQuotes.quotes[0];

      const quote = useBoostQuote && sdkQuote.boostQuote ? sdkQuote.boostQuote : sdkQuote;
      route = buildRouteObject(swapParams, quote, {
        defaultDurationSeconds: sdkQuote.estimatedDurationSeconds,
        hasBoostQuote: Boolean(sdkQuote.boostQuote),
      });
    }

    const duration = getSwapDuration(sdkStatus);

    const deposit = sdkStatus && 'deposit' in sdkStatus ? sdkStatus.deposit : undefined;

    let srcTransactionHash;
    if (deposit?.txRef) {
      srcTransactionHash = deposit.txRef;
    } else if (
      ['Ethereum', 'Arbitrum', 'Bsc'].includes(swapParams.srcChain) &&
      isHex(localTransactionHash ?? '')
    ) {
      srcTransactionHash = localTransactionHash;
    } else if (swapParams.srcChain === 'Solana' && isBase58(localTransactionHash)) {
      srcTransactionHash = localTransactionHash;
    } else if (swapParams.srcChain === 'Tron' && isTronTxHash(localTransactionHash)) {
      srcTransactionHash = localTransactionHash;
    }

    const srcConfirmationCount = deposit?.txConfirmations;

    let status = sdkStatus ? mapChainflipStatus(sdkStatus) : 'waiting_for_src_tx';
    if (
      status === 'waiting_for_src_tx' &&
      (srcTransactionHash || srcConfirmationCount !== undefined)
    ) {
      status = 'waiting_for_src_tx_confirmation'; // show receiving after tx was submitted via connected wallet
    }

    return {
      id: swapId,
      shareableId,
      integration: 'chainflip' as const,
      integrationData: sdkStatus,
      status,
      route,
      depositAddress: sdkStatus?.depositChannel?.depositAddress,
      srcTransactionHash,
      srcConfirmationCount,
      destAddress,
      destTransactionHash: undefined,
      duration,
    };
  };

  getOnChainSwapStatus = async (
    nativeSwapRequestId: string,
  ): Promise<ChainflipStatusResponse | undefined> => {
    const storedRoute = loadRouteFromLocalStorage('chainflip', nativeSwapRequestId);
    let sdkStatus: SwapStatusResponseV2 | undefined;

    if (nativeSwapRequestId) {
      try {
        sdkStatus = await this.sdk.getStatusV2({ id: nativeSwapRequestId });
      } catch (e) {
        // ignore 404: sdk will return status of transaction hash only after deposit is witnessed
        if (!(isAxiosError(e) && e.response?.status === 404)) throw e;
      }
    }

    let swapParams: Parameters<typeof buildRouteObject>[0];
    const fees =
      sdkStatus && 'fees' in sdkStatus && sdkStatus.fees
        ? sdkStatus.fees.filter((fee) => fee.type !== 'LIQUIDITY')
        : undefined;

    let route: ChainflipRouteResponse;
    if (sdkStatus && cfIsSwappingFinished(sdkStatus)) {
      swapParams = {
        ...sdkStatus,
        srcChain: sdkStatus.srcChain as ChainflipChain,
        destChain: sdkStatus.destChain as ChainflipChain,
        amount: 'swap' in sdkStatus ? sdkStatus.swap.originalInputAmount : '0',
      };
      route = buildRouteObject(
        swapParams,
        {
          intermediateAmount: sdkStatus.swap.swappedIntermediateAmount,
          egressAmount: getEgressAmount(sdkStatus) ?? '0',
          includedFees: fees ?? [],
          estimatedDurationSeconds: sdkStatus.estimatedDurationSeconds ?? 0,
          maxBoostFeeBps: sdkStatus.boost?.maxBoostFeeBps,
          dcaParams: sdkStatus.dcaParams,
        },
        {
          defaultDurationSeconds: 0,
          hasBoostQuote: false,
        },
      );
    } else if (storedRoute) {
      swapParams = getAssetsAndAmount(storedRoute);
      const numberOfChunks = sdkStatus?.dcaParams?.numberOfChunks;
      const sdkQuotes = await this.sdk.getQuoteV2(swapParams);
      const sdkQuote =
        sdkQuotes.quotes.find((q) => {
          const quoteChunks = q.type === 'DCA' ? q.dcaParams?.numberOfChunks : undefined;
          return quoteChunks === numberOfChunks;
        }) ?? sdkQuotes.quotes[0];

      route = buildRouteObject(swapParams, sdkQuote, {
        defaultDurationSeconds: sdkQuote.estimatedDurationSeconds,
        hasBoostQuote: false,
      });
    } else {
      return undefined;
    }

    const status = sdkStatus ? mapChainflipStatus(sdkStatus) : 'waiting_for_src_tx';

    return {
      id: nativeSwapRequestId,
      shareableId: nativeSwapRequestId,
      integration: 'chainflip' as const,
      integrationData: sdkStatus,
      status,
      route,
      depositAddress: undefined,
      srcTransactionHash: undefined,
      srcConfirmationCount: undefined,
      destAddress: undefined,
      destTransactionHash: undefined,
      duration: undefined,
    };
  };

  createDepositChannel = async (
    swapId: string,
    options: {
      srcAddress?: string;
      slippageParams: {
        mode: 'auto' | 'custom';
        livePriceSlippageTolerancePercent: number | undefined;
        slippageTolerancePercent: number;
        swapDeadlineMinutes: number;
      };
    },
  ) => {
    const preparedRoute = loadRouteFromLocalStorage('chainflip', swapId);
    if (!preparedRoute || preparedRoute.integration !== 'chainflip') {
      throw new Error(`Invalid route when opening deposit channel`);
    }
    if (!preparedRoute.integrationData.quote) {
      throw new Error(`Missing quote for prepared route when opening deposit channel`);
    }
    if (!preparedRoute.refundAddress) {
      throw new Error(`Missing refund address when opening deposit channel`);
    }

    const fillOrKillParams = {
      refundAddress: preparedRoute.refundAddress,
      livePriceSlippageTolerancePercent: options.slippageParams.livePriceSlippageTolerancePercent,
      retryDurationBlocks: options.slippageParams.swapDeadlineMinutes * 10,
      slippageTolerancePercent: options.slippageParams.slippageTolerancePercent,
    };

    const response = await this.sdk.requestDepositAddressV2({
      quote: preparedRoute.integrationData.quote,
      srcAddress: options.srcAddress,
      destAddress: preparedRoute.destAddress,
      fillOrKillParams,
    });
    storeDepositChannelIdInLocalStorage('chainflip', swapId, response.depositChannelId);

    return response;
  };

  private executeSwap = async (
    swapId: string,
    cb: (status: ChainflipStatusResponse) => Promise<string>,
  ) => {
    const status = await this.getStatus(swapId);
    if (!status) {
      throw new Error(`Status of swap "${swapId}" not found`);
    }
    if (status.route.integration !== 'chainflip') {
      throw new Error(`Unexpected route when executing swap "${swapId}"`);
    }
    if (!status.destAddress) {
      throw new Error(`Missing dest address when executing swap "${swapId}"`);
    }

    const transactionHash = await cb(status);

    storeTransactionHashInLocalStorage(status.shareableId ?? status.id, transactionHash);
    // store route data as sdk will return status for transaction hash only after deposit was witnessed
    storeRouteInLocalStorage('chainflip', transactionHash, {
      ...status.route,
      destAddress: status.destAddress,
    });

    return {
      integration: 'chainflip' as const,
      integrationData: transactionHash,
      error: undefined,
    };
  };

  executeEvmSwap = async (swapId: string, walletClient: WalletClient<Transport, Chain, Account>) =>
    this.executeSwap(swapId, async (status) => {
      if (!status.depositAddress) {
        throw new Error(`Missing deposit address when executing swap "${swapId}"`);
      }

      return status.route.srcToken.address === NATIVE_TOKEN_ADDRESS
        ? walletClient.sendTransaction({
            value: status.route.srcAmount.toBigInt(),
            to: status.depositAddress as `0x${string}`,
            chain: status.route.srcToken.chain.wagmiChain,
          })
        : walletClient.writeContract({
            address: status.route.srcToken.address as `0x${string}`,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [status.depositAddress as `0x${string}`, status.route.srcAmount.toBigInt()],
            chain: status.route.srcToken.chain.wagmiChain,
          });
    });

  async executeSolanaSwap(
    swapId: string,
    wallet: WalletContextState,
    connection: Connection,
  ): Promise<ExecuteSwapResponse> {
    return this.executeSwap(swapId, async (status) => {
      if (!wallet.publicKey) {
        throw new Error(`Missing public key when executing swap "${swapId}"`);
      }
      if (!status.depositAddress) {
        throw new Error(`Missing deposit address when executing swap "${swapId}"`);
      }

      const depositAddress = new PublicKey(status.depositAddress);

      let instructions;

      if (status.route.srcToken.chainflipId === 'Sol') {
        instructions = [
          SystemProgram.transfer({
            fromPubkey: wallet.publicKey,
            toPubkey: depositAddress,
            lamports: status.route.srcAmount.toBigInt(),
          }),
        ];
      } else {
        const mint = new PublicKey(status.route.srcToken.address);
        const associatedTokenFrom = await getAssociatedTokenAddress(mint, wallet.publicKey);
        const fromAccount = await getAccount(connection, associatedTokenFrom);
        const associatedTokenTo = await getAssociatedTokenAddress(mint, depositAddress, true);

        instructions = [
          createAssociatedTokenAccountIdempotentInstruction(
            wallet.publicKey,
            associatedTokenTo,
            depositAddress,
            mint,
          ),
          createTransferInstruction(
            fromAccount.address,
            associatedTokenTo,
            wallet.publicKey,
            status.route.srcAmount.toBigInt(),
          ),
        ];
      }

      const tx = new Transaction().add(...instructions);

      return wallet.sendTransaction(tx, connection);
    });
  }

  async executeTronSwap(swapId: string, fromAddress: string): Promise<ExecuteSwapResponse> {
    return this.executeSwap(swapId, async (status) => {
      if (!status.depositAddress) {
        throw new Error(`Missing deposit address when executing swap "${swapId}"`);
      }

      const { tronWeb } = window as Window & { tronWeb?: TronWeb };
      if (!tronWeb) throw new Error('TronLink is not installed or not connected');

      return executeTronTransfer({
        tronWeb,
        tokenAddress: status.route.srcToken.address,
        amount: status.route.srcAmount.toBigInt(),
        depositAddress: status.depositAddress,
        fromAddress,
      });
    });
  }
}
